import { isFeatureEnabledForField } from './fieldFlags';
import {
  DE2SupportedSuggestion,
  FieldSuggestionAlias,
} from 'types/DataExtraction';

// only block types with a Suggestible control at extraction time.
// other types like Heading, Subheading, Table never receive suggestions even
// if their label matches an alias
export const SUGGESTIBLE_BLOCK_TYPES = [
  'TextField',
  'MultipleChoice',
  'Checkboxes',
];

// mirror AutoDocumentExtractions::FieldAliases.normalize
const normalize = (label: string): string =>
  label.trim().toLowerCase().replace(/\s+/g, ' ');

export const buildSuggestionFieldMatcher = (
  supportedSuggestions: FieldSuggestionAlias[],
  hasFeature: (flag: string) => boolean,
  fieldFlags?: Record<string, string>
): ((label: string) => boolean) => {
  const fieldByAlias = new Map<string, string>();
  supportedSuggestions.forEach(({ field, aliases }) =>
    aliases.forEach((alias) => fieldByAlias.set(normalize(alias), field))
  );

  return (label) => {
    const field = fieldByAlias.get(normalize(label ?? ''));

    return (
      field !== undefined &&
      isFeatureEnabledForField(field, hasFeature, fieldFlags)
    );
  };
};

export const MIN_AUTOCOMPLETE_QUERY_LENGTH = 2;

export type SuggestionAutocompleteOption = {
  field: string;
  templateLabel: string;
};

export type FindSuggestionOptions = (
  label: string,
  blockType: string
) => SuggestionAutocompleteOption[];

export const buildSuggestionOptionsFinder = (
  supportedSuggestions: DE2SupportedSuggestion[],
  hasFeature: (flag: string) => boolean,
  fieldFlags?: Record<string, string>
): FindSuggestionOptions => {
  const fields = supportedSuggestions
    .filter(({ field }) =>
      isFeatureEnabledForField(field, hasFeature, fieldFlags)
    )
    .map(({ field, template_label, block_types, aliases }) => ({
      field,
      templateLabel: template_label,
      blockTypes: block_types,
      aliases: aliases.map(normalize),
    }));

  return (label, blockType) => {
    const query = normalize(label);

    if (query.length < MIN_AUTOCOMPLETE_QUERY_LENGTH) {
      return [];
    }
    if (fields.some(({ aliases }) => aliases.includes(query))) {
      return [];
    }

    return fields
      .filter(
        ({ blockTypes, aliases }) =>
          blockTypes.some((type) => type === blockType) &&
          aliases.some((alias) => alias.includes(query))
      )
      .map(({ field, templateLabel }) => ({ field, templateLabel }));
  };
};
